import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";

export default function ParanoidiFooter({
  showLoginLinks = false,
  forceDark = false,
}) {
  const { t, i18n } = useTranslation("common");
  const isEnglish = i18n.resolvedLanguage?.toLowerCase().startsWith("en");
  const year = new Date().getFullYear();

  return (
    <div className={forceDark ? "theme-dark" : ""}>
      <footer
        id="paranoidi-footer"
        className="relative overflow-hidden border-t border-[#5a3824]/15 bg-[#fff8ef] px-4 py-10 text-[#241a16] theme-dark:border-red-500/15 theme-dark:bg-black theme-dark:text-white sm:px-6 lg:px-8"
      >
        {/* Taustan kevyt hehku */}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(180,35,24,0.10),transparent_36%)] theme-dark:bg-[radial-gradient(circle_at_50%_0%,rgba(239,68,68,0.18),transparent_34%)]" />

        {/* Hento ruudukko */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.035] [background-image:linear-gradient(rgba(90,56,36,.8)_1px,transparent_1px),linear-gradient(90deg,rgba(90,56,36,.8)_1px,transparent_1px)] [background-size:42px_42px] theme-dark:opacity-[0.05] theme-dark:[background-image:linear-gradient(rgba(255,255,255,.8)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.8)_1px,transparent_1px)]" />

        <div className="relative mx-auto grid max-w-6xl gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(21rem,0.72fr)] lg:items-start">
          {/* Vasen puoli */}
          <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
            <a
              href={isEnglish ? "/en/" : "/"}
              aria-label={t("navigation.homeLabel")}
              className="group inline-flex items-center gap-3 no-underline"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full border border-red-300 bg-red-50 shadow-[0_0_22px_rgba(180,35,24,0.12)] transition duration-300 group-hover:border-red-400 group-hover:bg-red-100 group-hover:shadow-[0_0_30px_rgba(180,35,24,0.20)] theme-dark:border-red-400/25 theme-dark:bg-red-500/10 theme-dark:shadow-[0_0_22px_rgba(239,68,68,0.22)] theme-dark:group-hover:border-red-400/50 theme-dark:group-hover:bg-red-500/15 theme-dark:group-hover:shadow-[0_0_30px_rgba(239,68,68,0.34)]">
                <img
                  src="/brand/scroll-to-top-icon.png"
                  alt=""
                  className="h-8 w-8 object-contain"
                  aria-hidden="true"
                />
              </span>

              <span className="text-xl font-black uppercase tracking-[-0.04em] text-[#241a16] theme-dark:text-white">
                Paranoidi
              </span>
            </a>

            <p className="mt-4 max-w-2xl text-sm font-semibold leading-7 text-[#6b4229] theme-dark:text-zinc-400">
              {t("footer.description")}
            </p>

            {/* Paranoidin sosiaalisen median kanavat */}
            <div className="mt-5 flex items-center justify-center gap-3 lg:justify-start">
              <a
                href="https://www.instagram.com/paranoidiofficial/"
                target="_blank"
                rel="noreferrer"
                aria-label="Paranoidi Instagram"
                title="Instagram"
                className="group flex h-10 w-10 items-center justify-center rounded-full border border-[#5a3824]/15 bg-white/60 text-[#6b4229] shadow-sm transition duration-300 hover:-translate-y-0.5 hover:border-red-300 hover:bg-red-50 hover:text-red-700 hover:shadow-[0_8px_24px_rgba(127,29,29,0.12)] theme-dark:border-white/10 theme-dark:bg-white/[0.03] theme-dark:text-zinc-400 theme-dark:hover:border-red-400/40 theme-dark:hover:bg-red-500/10 theme-dark:hover:text-red-200 theme-dark:hover:shadow-[0_8px_24px_rgba(239,68,68,0.16)]"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  aria-hidden="true"
                >
                  <rect
                    x="3.5"
                    y="3.5"
                    width="17"
                    height="17"
                    rx="5"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />

                  <circle
                    cx="12"
                    cy="12"
                    r="4"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />

                  <circle
                    cx="17.4"
                    cy="6.7"
                    r="1"
                    fill="currentColor"
                  />
                </svg>
              </a>

              <a
                href="https://www.tiktok.com/@paranoidiofficial"
                target="_blank"
                rel="noreferrer"
                aria-label="Paranoidi TikTok"
                title="TikTok"
                className="group flex h-10 w-10 items-center justify-center rounded-full border border-[#5a3824]/15 bg-white/60 text-[#6b4229] shadow-sm transition duration-300 hover:-translate-y-0.5 hover:border-red-300 hover:bg-red-50 hover:text-red-700 hover:shadow-[0_8px_24px_rgba(127,29,29,0.12)] theme-dark:border-white/10 theme-dark:bg-white/[0.03] theme-dark:text-zinc-400 theme-dark:hover:border-red-400/40 theme-dark:hover:bg-red-500/10 theme-dark:hover:text-red-200 theme-dark:hover:shadow-[0_8px_24px_rgba(239,68,68,0.16)]"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  aria-hidden="true"
                >
                  <path
                    d="M14.5 4.5v9.1a4.6 4.6 0 1 1-3.8-4.52"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />

                  <path
                    d="M14.5 4.5c.55 2.45 2.05 3.9 4.4 4.35"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            </div>

            <LanguageSwitcher variant="public" className="mt-6" />

            <nav
              aria-label={t("navigation.publicLinks")}
              className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-3 text-xs font-bold text-[#8a5a35] theme-dark:text-zinc-500 lg:justify-start"
            >
              <a
                href={isEnglish ? "/en/features" : "/ominaisuudet"}
                className="underline-offset-4 transition hover:text-[#241a16] hover:underline theme-dark:hover:text-white"
              >
                {t("navigation.features")}
              </a>

              <a
                href={
                  isEnglish
                    ? "/en/song-requests"
                    : "/biisitoiveet-keikalle"
                }
                className="underline-offset-4 transition hover:text-[#241a16] hover:underline theme-dark:hover:text-white"
              >
                {t("navigation.songRequests")}
              </a>

              {isEnglish && (
                <a
                  href="/en/setlist-app-for-musicians"
                  className="underline-offset-4 transition hover:text-[#241a16] hover:underline theme-dark:hover:text-white"
                >
                  Setlist app
                </a>
              )}

              <a
                href={isEnglish ? "/en/privacy" : "/tietosuoja"}
                className="underline-offset-4 transition hover:text-[#241a16] hover:underline theme-dark:hover:text-white"
              >
                {t("navigation.privacy")}
              </a>

              <a
                href={isEnglish ? "/en/terms" : "/kayttoehdot"}
                className="underline-offset-4 transition hover:text-[#241a16] hover:underline theme-dark:hover:text-white"
              >
                {t("navigation.terms")}
              </a>

              {showLoginLinks && (
                <a
                  href="/login"
                  className="underline-offset-4 transition hover:text-[#241a16] hover:underline theme-dark:hover:text-white"
                >
                  {t("navigation.login")}
                </a>
              )}
            </nav>
          </div>

          {/* Oikea puoli */}
          <div className="flex w-full flex-col items-center gap-4 lg:items-end">
            <a
              href={`mailto:info@buukkeri.fi?subject=${encodeURIComponent(
                t("footer.emailSubject")
              )}`}
              aria-label={t("footer.emailLabel", {
                email: "info@buukkeri.fi",
              })}
              className="group flex w-full max-w-md items-center gap-4 overflow-hidden rounded-[1.5rem] border border-red-200 bg-gradient-to-r from-red-50 via-white/80 to-transparent px-5 py-4 text-left no-underline shadow-[0_16px_45px_rgba(58,43,24,0.12)] transition duration-300 hover:-translate-y-0.5 hover:border-red-300 hover:shadow-[0_20px_50px_rgba(127,29,29,0.16)] theme-dark:border-red-400/20 theme-dark:from-red-500/[0.12] theme-dark:via-white/[0.035] theme-dark:to-transparent theme-dark:shadow-[0_16px_45px_rgba(0,0,0,0.34)] theme-dark:hover:border-red-400/45 theme-dark:hover:shadow-[0_20px_50px_rgba(127,29,29,0.24)]"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-red-300 bg-red-100 text-red-700 transition duration-300 group-hover:border-red-400 group-hover:bg-red-200 group-hover:text-red-800 theme-dark:border-red-400/25 theme-dark:bg-red-500/15 theme-dark:text-red-200 theme-dark:group-hover:border-red-300/55 theme-dark:group-hover:bg-red-500/25 theme-dark:group-hover:text-white">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  aria-hidden="true"
                >
                  <path
                    d="M4 6.5h16v11H4v-11Z"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinejoin="round"
                  />

                  <path
                    d="m5 7.5 7 5 7-5"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>

              <span className="min-w-0 flex-1">
                <span className="block text-[0.65rem] font-black uppercase tracking-[0.18em] text-red-700 theme-dark:text-red-300">
                  {t("footer.questions")}
                </span>

                <span className="mt-1 block truncate text-sm font-black text-[#241a16] theme-dark:text-white sm:text-base">
                  info@buukkeri.fi
                </span>

                <span className="mt-1 block text-xs font-semibold text-[#8a5a35] theme-dark:text-zinc-500">
                  {t("footer.sendEmail")}
                </span>
              </span>

              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 shrink-0 text-[#8a5a35] transition duration-300 group-hover:translate-x-1 group-hover:text-red-700 theme-dark:text-zinc-600 theme-dark:group-hover:text-red-300"
                aria-hidden="true"
              >
                <path
                  d="m9 6 6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>

            <div className="flex items-center gap-3 rounded-full border border-[#5a3824]/15 bg-white/60 px-4 py-2 theme-dark:border-white/10 theme-dark:bg-white/[0.03]">
              <span className="text-[0.65rem] font-black uppercase tracking-[0.18em] text-[#8a5a35] theme-dark:text-zinc-500">
                Powered by Buukkeri
              </span>

              <img
                src="/assets/buukkeri/buukkeri-logo.webp"
                alt="Buukkeri"
                className="h-6 w-auto opacity-90"
              />
            </div>

            <div className="text-center text-xs leading-6 text-[#8a5a35] theme-dark:text-zinc-500 lg:text-right">
              <p>
                {t("footer.createdBy")}{" "}
                <a
                  href="https://arttustenius.com"
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-[#241a16] underline-offset-4 transition hover:text-red-700 hover:underline theme-dark:text-white theme-dark:hover:text-red-200"
                >
                  Arttu Stenius
                </a>
              </p>

              <p className="text-[#8a5a35]/75 theme-dark:text-zinc-600">
                &copy; {year} Paranoidi
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

